import { useState } from "react";
import {
  LogOut,
  User,
  Mail,
  Phone,
  Building2,
  Warehouse,
  Briefcase,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { resetFilters } from "../store/slices/filterSlice";
import { NavLink, useLocation } from "react-router-dom";
import { Drawer, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { SUPER_ADMIN, VISO_SUPER_ADMIN } from "../constants";
import {
  selectIsYard,
  selectIsWarehouse,
  selectIsPlant,
  selectCanSwitchModules,
} from "../store/slices/moduleSlice";

const linkClass = ({ isActive }) =>
  `flex items-center gap-3 px-4 py-2 rounded ${
    isActive ? "border-l-4 border-white bg-blue-600" : "hover:bg-gray-800"
  }`;

const getSafeValue = (key, fallback = "") => {
  const value = localStorage.getItem(key);
  return value && value !== "null" && value !== "undefined" ? value : fallback;
};

function Sidebar() {
  const dispatch = useDispatch();
  const location = useLocation();
  const [openProfile, setOpenProfile] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);

  const isYard = useSelector(selectIsYard);
  const isWarehouse = useSelector(selectIsWarehouse);
  const isPlant = useSelector(selectIsPlant);
  const canSwitchModules = useSelector(selectCanSwitchModules);

  const siteLabel = isPlant ? "Plant" : isWarehouse ? "Warehouse" : "Yard";

  const firstName = getSafeValue("first_name", "User");
  const lastName = getSafeValue("last_name");
  const companyName = getSafeValue("company_name");
  const email = getSafeValue("email");
  const phoneNumber = getSafeValue("phone_number");
  const role = getSafeValue("role_name");
  const userName = getSafeValue("user_name");
  const siteName = getSafeValue("yard_name");
  const companyId = getSafeValue("company_id");

  const logoSrc =
    getSafeValue("company_logo_base64") || getSafeValue("company_logo");
  const showLogo = Boolean(logoSrc) && !logoFailed;

  if (location.pathname === "/") return null;

  const firstLetter = firstName?.charAt(0)?.toUpperCase() || "?";

  const logout = () => {
    setOpenProfile(false);
    localStorage.clear();
    dispatch(resetFilters());
    window.location.href = "/";
  };

  const renderCompanyLink = () => {
    if (role === VISO_SUPER_ADMIN) {
      return (
        <NavLink to="/company" className={linkClass}>
          🏢 <span>Company Management</span>
        </NavLink>
      );
    }
    if (role === SUPER_ADMIN) {
      return (
        <NavLink
          to={`/company/${companyId}/yards`}
          state={{ company_name: companyName }}
          className={linkClass}
        >
          🏢 <span>Company Management</span>
        </NavLink>
      );
    }
    return null;
  };

  return (
    <>
      <div className="w-64 h-full bg-gray-900 text-white px-6 py-6 flex flex-col justify-between">
        <nav className="space-y-4">
          {showLogo ? (
            <div className="mb-6">
              <div
                className="rounded-xl flex items-center justify-center px-4"
                style={{
                  height: 60,
                  background: "rgba(255,255,255,0.80)",
                  boxShadow: "0 0 0 1px rgba(255,255,255,0.06)",
                }}
              >
                <img
                  src={logoSrc}
                  alt={companyName ? `${companyName} logo` : "Company logo"}
                  style={{
                    maxHeight: 30,
                    maxWidth: "100%",
                    objectFit: "contain",
                  }}
                  onError={() => setLogoFailed(true)}
                />
              </div>
              <div className="h-px bg-white/10 mt-5" />
            </div>
          ) : (
            <h1 className="text-2xl font-bold mb-8 px-2">VISOTONICS</h1>
          )}

          <NavLink to="/home" className={linkClass}>
            🏠 <span>Dashboard</span>
          </NavLink>

          <NavLink to="/report" className={linkClass}>
            📊 <span>Report</span>
          </NavLink>

          {isYard && (
            <NavLink to="/shipping_line" className={linkClass}>
              ⚓ <span>Shipping Line</span>
            </NavLink>
          )}

          {renderCompanyLink()}
        </nav>

        <div>
          <button
            onClick={() => setOpenProfile(true)}
            className="w-full flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-gray-800 transition-colors focus:outline-none"
          >
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white flex items-center justify-center text-sm font-bold flex-shrink-0 shadow-lg">
              {firstLetter}
            </div>
            <div className="flex-1 text-left overflow-hidden">
              <p className="text-sm font-medium truncate text-white">
                {firstName}
              </p>
              <p className="text-xs text-gray-400 truncate">
                {role || companyName || "View Profile"}
              </p>
            </div>
          </button>

          {showLogo && (
            <div
              className="mt-4 pt-3 border-t border-white/10 text-center text-gray-400"
              style={{ fontSize: 11, letterSpacing: "0.06em" }}
            >
              POWERED BY{" "}
              <span
                className="font-extrabold text-gray-200"
                style={{ letterSpacing: "0.08em" }}
              >
                VISOTONICS
              </span>
            </div>
          )}
        </div>
      </div>

      <Drawer
        anchor="right"
        open={openProfile}
        sx={{ "& .MuiDrawer-paper": { width: 300, maxWidth: "100%" } }}
        onClose={() => setOpenProfile(false)}
      >
        <div className="h-full bg-gradient-to-b from-blue-50 to-white">
          <div className="bg-white px-6 py-5 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-800">Profile</h2>
            <IconButton
              onClick={() => setOpenProfile(false)}
              size="small"
              sx={{
                color: "#6b7280",
                "&:hover": { backgroundColor: "#f3f4f6" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </div>

          <div className="p-6">
            <div className="flex flex-col items-center mb-6">
              <div className="h-24 w-24 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 text-white flex items-center justify-center text-4xl font-bold shadow-lg mb-3">
                {firstLetter}
              </div>
              <h3 className="font-bold text-xl text-gray-800 text-center mb-2">
                {firstName} {lastName}
              </h3>
              {companyName && (
                <div className="flex items-center gap-2">
                  <Building2 size={16} className="text-blue-600" />
                  <p className="text-sm text-gray-600">{companyName}</p>
                </div>
              )}
            </div>

            <div className="space-y-3">
              {email ? (
                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-4">
                    <div className="bg-blue-100 p-2.5 rounded-lg">
                      <Mail size={20} className="text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-gray-500 mb-1 font-medium">
                        Email
                      </p>
                      <p className="text-sm text-gray-800 font-medium truncate">
                        {email}
                      </p>
                    </div>
                  </div>
                </div>
              ) : phoneNumber ? (
                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                  <div className="flex items-center gap-4">
                    <div className="bg-green-100 p-2.5 rounded-lg">
                      <Phone size={20} className="text-green-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 mb-1 font-medium">
                        Phone
                      </p>
                      <p className="text-sm text-gray-800 font-medium">
                        {phoneNumber}
                      </p>
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-4">
                  <div className="bg-purple-100 p-2.5 rounded-lg">
                    <User size={20} className="text-purple-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 mb-1 font-medium">
                      User Name
                    </p>
                    <p className="text-sm text-gray-800 font-medium">
                      {userName}
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-4">
                  <div className="bg-orange-100 p-2.5 rounded-lg">
                    <Briefcase size={20} className="text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 mb-1 font-medium">
                      Role
                    </p>
                    <p className="text-sm text-gray-800 font-medium">{role}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
                <div className="flex items-center gap-4">
                  <div className="bg-blue-100 p-2.5 rounded-lg">
                    <Warehouse size={20} className="text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-xs text-gray-500 mb-1 font-medium">
                      {siteLabel}
                    </p>
                    <p className="text-sm text-gray-800 font-medium">
                      {siteName || "—"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-gray-200">
                <button
                  onClick={logout}
                  className="w-full flex items-center justify-center gap-3 bg-red-50 hover:bg-red-600 text-red-600 hover:text-white rounded-xl p-4 transition-all shadow-sm hover:border-red-600"
                >
                  <LogOut size={20} />
                  <span className="font-semibold">Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </Drawer>
    </>
  );
}

export default Sidebar;
